import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * EmailSettingsCard — Org Settings panel for transactional email
 * sender identity (multi-tenant "feel").
 *
 * The platform sends every email through a shared one.com mailbox
 * (noreply@kidswiseapp.com) but the visible From-name and Reply-To
 * are configurable per organisation so parents perceive each email
 * as coming from their own nursery. This card lets an admin:
 *
 *   • Set a custom **From-name** (e.g. "Little Stars Childcare").
 *   • Set a custom **Reply-To** address (defaults to org.contact_email).
 *   • Fire a **test email** to themselves to verify SMTP wiring.
 *   • View the most recent send log to debug missing emails.
 *
 * SMTP credentials live in the backend .env — never exposed here.
 */
export function EmailSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [senderName, setSenderName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [dirty, setDirty] = useState(false);
  const [log, setLog] = useState([]);
  const [logExpanded, setLogExpanded] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.getMyOrgEmailSettings();
      setSettings(data);
      setSenderName(data.email_sender_name || '');
      setReplyTo(data.email_reply_to || '');
      setDirty(false);
    } catch (err) {
      toast.error(err.message || 'Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const loadLog = async () => {
    try {
      const rows = await api.getOrgEmailLog(20);
      setLog(rows || []);
    } catch (err) {
      // non-fatal
      console.warn('Could not load email log', err);
    }
  };

  useEffect(() => {
    load();
    loadLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await api.updateMyOrgEmailSettings({
        email_sender_name: senderName.trim(),
        email_reply_to: replyTo.trim(),
      });
      setSettings(updated);
      setDirty(false);
      toast.success('Email sender identity saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const result = await api.sendOrgTestEmail();
      if (result?.ok) {
        toast.success('Test email sent — check your inbox in a moment.');
      } else {
        toast.error(result?.error || 'Send failed');
      }
      // Refresh log to show the new entry.
      loadLog();
    } catch (err) {
      toast.error(err.message || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="email-settings-card">
        <div style={{ padding: '1rem', color: '#6b7280' }}>Loading email settings…</div>
      </div>
    );
  }

  const smtpOk = !!settings?.smtp_configured;

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="email-settings-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <div>
          <h3 style={{ margin: 0 }}>📧 Email Notifications</h3>
          <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            How transactional emails (booking confirmations, broadcasts, password resets) look to your parents and staff.
          </p>
        </div>
        <span
          data-testid="smtp-status-badge"
          style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            background: smtpOk ? '#dcfce7' : '#fef3c7',
            color: smtpOk ? '#166534' : '#92400e',
          }}
        >
          {smtpOk ? 'SMTP connected' : 'SMTP not configured'}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }} className="email-settings-grid">
        <div>
          <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>From-name (visible to recipient)</label>
          <input
            type="text"
            data-testid="email-sender-name-input"
            value={senderName}
            placeholder={settings?.org_name || 'Your organisation name'}
            onChange={(e) => { setSenderName(e.target.value); setDirty(true); }}
            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '0.25rem' }}
          />
          <small style={{ color: '#6b7280' }}>
            Shown as <strong>{senderName.trim() || settings?.org_name || 'Kids WiseApp'}</strong> &lt;{settings?.platform_from_address}&gt;
          </small>
        </div>

        <div>
          <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Reply-To address</label>
          <input
            type="email"
            data-testid="email-reply-to-input"
            value={replyTo}
            placeholder={settings?.org_contact_email || 'admin@yourorg.example'}
            onChange={(e) => { setReplyTo(e.target.value); setDirty(true); }}
            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '0.25rem' }}
          />
          <small style={{ color: '#6b7280' }}>
            When parents hit "Reply" their message goes here. Defaults to your org contact email.
          </small>
        </div>
      </div>

      <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <strong>Effective sender preview</strong>
            <div style={{ color: '#374151', marginTop: '0.25rem' }}>
              From: <code>{settings?.effective_from_name} &lt;{settings?.platform_from_address}&gt;</code>
            </div>
            <div style={{ color: '#374151' }}>
              Reply-To: <code>{settings?.effective_reply_to || '—'}</code>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        <button
          data-testid="email-settings-save-button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="btn btn-primary"
          style={{
            padding: '0.5rem 1rem',
            background: !dirty || saving ? '#9ca3af' : '#00BCD4',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: !dirty || saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
          }}
        >
          {saving ? 'Saving…' : '💾 Save sender identity'}
        </button>

        <button
          data-testid="email-settings-test-button"
          onClick={handleTest}
          disabled={!smtpOk || testing}
          style={{
            padding: '0.5rem 1rem',
            background: !smtpOk || testing ? '#9ca3af' : '#10b981',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: !smtpOk || testing ? 'not-allowed' : 'pointer',
            fontWeight: 600,
          }}
          title={!smtpOk ? 'SMTP is not configured on the backend yet' : 'Send a branded test email to yourself'}
        >
          {testing ? 'Sending…' : '📨 Send test email to me'}
        </button>

        <button
          data-testid="email-settings-toggle-log"
          onClick={() => { setLogExpanded((s) => !s); if (!logExpanded) loadLog(); }}
          style={{
            padding: '0.5rem 1rem',
            background: '#fff',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          {logExpanded ? 'Hide recent emails' : 'Show recent emails'}
        </button>
      </div>

      {logExpanded && (
        <div style={{ marginTop: '1rem' }} data-testid="email-log-table-wrapper">
          {log.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: '0.875rem', padding: '0.75rem' }}>No emails sent yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem' }}>When</th>
                    <th style={{ padding: '0.5rem' }}>To</th>
                    <th style={{ padding: '0.5rem' }}>Subject</th>
                    <th style={{ padding: '0.5rem' }}>Template</th>
                    <th style={{ padding: '0.5rem' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((row) => (
                    <tr key={row._id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                        {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '0.5rem' }}>{row.to}</td>
                      <td style={{ padding: '0.5rem' }}>{row.subject}</td>
                      <td style={{ padding: '0.5rem', color: '#6b7280' }}>{row.template}</td>
                      <td style={{ padding: '0.5rem' }}>
                        <span style={{
                          padding: '0.15rem 0.5rem',
                          borderRadius: '999px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          background: row.status === 'sent' ? '#dcfce7' : row.status === 'skipped' ? '#fef3c7' : '#fee2e2',
                          color: row.status === 'sent' ? '#166534' : row.status === 'skipped' ? '#92400e' : '#991b1b',
                        }}>
                          {row.status}
                        </span>
                        {row.error && (
                          <div style={{ color: '#991b1b', fontSize: '0.7rem', marginTop: '0.15rem' }}>{row.error}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EmailSettingsCard;
